import React, {FC, useState, useMemo, useContext, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Fund} from '../types/Fund'
import {ILanguage, IMode} from '../types/common'
import {sortRows, filterRows, paginateRows, usePagination, isBoolean, COLUMNS, invRor} from './Helpers'
import FundTableRow from './FundTableRow'
import FundTableHeaderCell from './FundTableHeaderCell'
import LimitSelectDropdown from './LimitSelectDropdown'
import VisibleColumnsSelectDropdown, {IVisibleColumnsOption} from './VisibleColumnsSelectDropdown'
import CompareButton from './CompareButton'
import FiltersALL from '../FundCatalogApp/filters/FiltersALL'
import FiltersFIO from '../FundCatalogApp/filters/FiltersFIO'
import FiltersUFK from '../FundCatalogApp/filters/FiltersUFK'
import FiltersOFE from '../FundCatalogApp/filters/FiltersOFE'
import FiltersDFE from '../FundCatalogApp/filters/FiltersDFE'
import SVGIcon from './SVGIcon'
import SimpleBar from 'simplebar-react'
import {GlobalContext} from './ctx/GlobalContext'
import {Oval} from 'react-loader-spinner'
import {format, isSameDay} from 'date-fns'
import {useTimeout} from 'usehooks-ts'
import usePrevious from '../hooks/usePrevious'
import {FundChartData} from '../types/FundChartData'
import * as XLSX from 'xlsx-js-style'
import layout from '../FundProfileApp/Layout'
import {rorListDownload} from './XlsxTools'

const FundTable: FC<{
    data: Array<Fund>
    mode: IMode
    isLoading: boolean
    ctx: any
    language: ILanguage
    dynamicColumns: object
    filters?: object
}> = ({data, mode, ctx, language, dynamicColumns, isLoading}) => {
    const {t} = useTranslation()
    const globalCtx = useContext(GlobalContext)
    const [activePage, setActivePage] = useState(1)
    const [selectedRateOfReturnOptions, setSelectedRateOfReturnOptions] = useState<Array<string>>([])
    const scrollTopRef = useRef(null)
    const scrollTopContentRef = useRef(null)
    const scrollBottomRef = useRef(null)
    const scrollBottomContentRef = useRef(null)
    const [scrollTopContentWidth, setScrollTopContentWidth] = useState(0)
    const previousDate: unknown = usePrevious(ctx.date)
    const previousToday: unknown = usePrevious(ctx.today)

    const rateOfReturnOptions: Array<IVisibleColumnsOption> = Object.entries(dynamicColumns).map(
        ([, item]): IVisibleColumnsOption => {
            return item as IVisibleColumnsOption
        }
    )

    useTimeout(() => {
        handleResize()
    }, 500)

    const handleResize = () => {
        const {width} = scrollBottomContentRef.current.getBoundingClientRect()
        setScrollTopContentWidth(width)
    }

    useEffect(() => {
        handleResize()

        window.addEventListener('resize', handleResize)
        scrollTopRef.current.recalculate()
        scrollBottomRef.current.recalculate()

        scrollTopRef.current.getScrollElement().addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
            scrollBottomRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
        })

        scrollBottomRef.current
            .getScrollElement()
            .addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
                scrollTopRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
            })

        return () => {
            window.removeEventListener('resize', handleResize)
            scrollTopRef.current.getScrollElement().removeEventListener('scroll', () => {})
            scrollBottomRef.current.getScrollElement().removeEventListener('scroll', () => {})
        }
    })

    useEffect(() => {
        setSelectedRateOfReturnOptions(ctx.columns)
    }, [ctx.columns])

    const handleDataSort = (sortCol: string): void => {
        if (sortCol !== ctx.sortCol) {
            ctx.setSortCol(sortCol)
            ctx.setSortDir('ASC')
            return
        }

        if (sortCol === ctx.sortCol && ctx.sortDir === 'ASC') {
            ctx.setSortCol(sortCol)
            ctx.setSortDir('DESC')
            return
        }

        if (sortCol === ctx.sortCol && ctx.sortDir === 'DESC') {
            ctx.setSortCol('name')
            ctx.setSortDir('ASC')
            return
        }
    }

    const handleSortByName = (sortCol: string): void => {
        if ('name' !== ctx.sortCol) {
            ctx.setSortCol(sortCol)
            ctx.setSortDir('ASC')
            return
        }

        ctx.setSortCol(sortCol)
        ctx.setSortDir((prevValue: string) => {
            return prevValue === 'ASC' ? 'DESC' : 'ASC'
        })
    }

    const handleLimitChange = (value: number): void => {
        ctx.setLimit(value)
        setActivePage(1)
    }

    const handleSelectedRateOfReturnColumns = (value: string): void => {
        ctx.setColumns((prevSelected: Array<string>) => {
            handleResize()
            const newArray = [...prevSelected]
            if (newArray.includes(value)) {
                return newArray.filter((item) => item != value)
            } else {
                newArray.push(value)
                return newArray
            }
        })
    }

    const handleFilter = (value: any, field: string): void => {
        setActivePage(1)
        if ((value && value.length > 0) || (isBoolean(value) && value === true)) {
            ctx.setActiveFilters((prevFilters: object) => ({
                ...prevFilters,
                [field]: value,
            }))
        } else {
            ctx.setActiveFilters((prevFilters: object) => {
                const updatedFilters: Record<string, string> = {...prevFilters}
                delete updatedFilters[field]
                return updatedFilters
            })
        }
    }

    const filteredRows = useMemo(() => filterRows(data, ctx.activeFilters), [data, ctx.activeFilters])
    const sortedRows = useMemo(
        () => sortRows(filteredRows, {sortDir: ctx.sortDir, sortCol: ctx.sortCol}, language, ctx.favorites),
        [filteredRows, ctx.favorites, {sortDir: ctx.sortDir, sortCol: ctx.sortCol}]
    )
    const calculatedRows = paginateRows(sortedRows, activePage, ctx.limit)

    const count = filteredRows.length

    const totalPages = count < 2 ? 1 : Math.ceil(count / ctx.limit)

    const paginationRange = usePagination({
        totalPages,
        siblingCount: 1,
        activePage,
    })

    function handleSelectedRateOfReturnColumnsReset() {
        handleResize()
        ctx.setColumns(COLUMNS)
    }

    function isLoader(): boolean {
        // ALL-74 - current valuations loader fix
        if (ctx.date === null && previousToday !== null && previousToday !== undefined) {
            return !(previousDate === null)
        }

        return !(!ctx.date || isSameDay(ctx.today, ctx.date))
    }

    const countFavourite = () => {
        if (globalCtx.layout === 'all' && ctx.favorites.length > 0) {
            const visibleFavorites: Array<Fund> = sortedRows.filter((item: Fund) => ctx.favorites.includes(item.id))
            return t('label.favourite.length', {count: visibleFavorites.length})
        }
    }

    const downloadList = () => {
        const fileDate = new Date()
        const headerTitle = t('label.xlsx.list.title.' + globalCtx.layout)
        const filename =
            'Allianz.pl - ' + t('label.xlsx.list.filename.' + globalCtx.layout) + ' - ' + fileDate.getTime() + '.xlsx'

        rorListDownload(sortedRows, selectedRateOfReturnOptions, t, globalCtx.layout, headerTitle, filename, fileDate)
    }

    return (
        <>
            {' '}
            {globalCtx.layout === 'all' && <FiltersALL language={language} handleFilter={handleFilter} ctx={ctx} />}
            {globalCtx.layout === 'fio' && <FiltersFIO language={language} handleFilter={handleFilter} ctx={ctx} />}
            {globalCtx.layout === 'ufk' && <FiltersUFK language={language} handleFilter={handleFilter} ctx={ctx} />}
            {globalCtx.layout === 'ofe' && <FiltersOFE language={language} ctx={ctx} />}
            {globalCtx.layout === 'dfe' && <FiltersDFE language={language} ctx={ctx} />}
            <div className="fund-table" id="fund-table">
                <div className="results-sort display-none flex">
                    <div className="results-funds flex">
                        {globalCtx.layout !== 'ofe' && globalCtx.layout !== 'dfe' && (
                            <span>
                                {t('label.rows.length', {count: filteredRows.length})}
                                {countFavourite()}
                            </span>
                        )}
                    </div>

                    <VisibleColumnsSelectDropdown
                        options={rateOfReturnOptions}
                        selected={selectedRateOfReturnOptions}
                        toggleOption={handleSelectedRateOfReturnColumns}
                        handleReset={handleSelectedRateOfReturnColumnsReset}
                    />
                </div>
                {(globalCtx.layout === 'fio' || globalCtx.layout === 'ufk') && ctx.setActiveTab && (
                    <div className="btn-copmparison-rwd">
                        <CompareButton selected={ctx.compared.length} setActiveTab={ctx.setActiveTab} />
                    </div>
                )}

                {isLoader() && (
                    <>
                        <Oval
                            visible={true}
                            height="44"
                            width="42"
                            color="#007AB3"
                            ariaLabel="oval-loading"
                            wrapperStyle={{}}
                            wrapperClass="spiner"
                        />
                    </>
                )}
                <div className="move-scrollbar"> {t('label.scroll.more')}</div>
                <SimpleBar
                    autoHide={false}
                    forceVisible={false}
                    scrollbarMinSize={0}
                    scrollbarMaxSize={1118}
                    ref={scrollTopRef}>
                    <div
                        className="scrollbar-top"
                        style={{width: scrollTopContentWidth}}
                        ref={scrollTopContentRef}></div>
                </SimpleBar>

                <SimpleBar
                    autoHide={false}
                    forceVisible={false}
                    scrollbarMinSize={0}
                    scrollbarMaxSize={1118}
                    ref={scrollBottomRef}>
                    <div className="fund-table-all">
                        <div className="fund-table-row display-none flex">
                            <div className="fund-wrapper" ref={scrollBottomContentRef}>
                                <div
                                    className={
                                        'row-sort row-wrapper-border' +
                                        (globalCtx.layout === 'dfe' ? ' row-sort-dfe' : '') +
                                        (globalCtx.layout === 'ufk' ? ' row-sort-ufk' : '')
                                    }>
                                    {(globalCtx.layout === 'all' || globalCtx.layout === 'ofe') && (
                                        <div className="checkbox flex" style={{paddingLeft: '0'}}>
                                            {' '}
                                        </div>
                                    )}

                                    <div
                                        className={
                                            'results-sort-rwd ' +
                                            (globalCtx.layout !== 'fio' && globalCtx.layout !== 'ufk'
                                                ? 'results-sort-dfe'
                                                : '')
                                        }>
                                        <div className="results-funds flex">
                                            {globalCtx.layout !== 'ofe' && globalCtx.layout !== 'dfe' && (
                                                <span>
                                                    {t('label.rows.length', {count: filteredRows.length})}
                                                    {countFavourite()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {(globalCtx.layout === 'fio' || globalCtx.layout === 'ufk') && ctx.setActiveTab && (
                                        <div className="btn-copmparison-desktop">
                                            <CompareButton
                                                selected={ctx.compared.length}
                                                setActiveTab={ctx.setActiveTab}
                                            />
                                        </div>
                                    )}
                                    {globalCtx.layout === 'dfe' && (
                                        <FundTableHeaderCell
                                            handleDataSort={handleSortByName}
                                            val="name"
                                            label={t('label.column.unitCategory')}
                                            className="checkbox checkbox-dfe flex"
                                            ctxSortCol={ctx.sortCol}
                                            ctxSortDir={ctx.sortDir}
                                        />
                                    )}

                                    <div className="row-price flex">
                                        {(globalCtx.layout === 'ofe' || globalCtx.layout === 'dfe') && (
                                            <FundTableHeaderCell
                                                handleDataSort={handleDataSort}
                                                val="valuation"
                                                label={t('label.column.valuation.ofe')}
                                                className="sort-value flex sort-value-fe"
                                                ctxSortCol={ctx.sortCol}
                                                ctxSortDir={ctx.sortDir}
                                            />
                                        )}
                                        {(globalCtx.layout === 'all' ||
                                            globalCtx.layout === 'fio' ||
                                            globalCtx.layout === 'ufk') && (
                                            <FundTableHeaderCell
                                                handleDataSort={handleDataSort}
                                                val="valuation"
                                                label={t('label.column.valuation')}
                                                className={
                                                    'sort-value flex' +
                                                    (globalCtx.layout === 'ufk' ? ' sort-value-ufk' : '')
                                                }
                                                ctxSortCol={ctx.sortCol}
                                                ctxSortDir={ctx.sortDir}
                                            />
                                        )}

                                        {globalCtx.layout === 'fio' && (
                                            <FundTableHeaderCell
                                                handleDataSort={handleDataSort}
                                                val="sri"
                                                label="SRI"
                                                className="sort-period flex"
                                                ctxSortCol={ctx.sortCol}
                                                ctxSortDir={ctx.sortDir}
                                            />
                                        )}

                                        {Object.entries(dynamicColumns).map(([key, item], index: number) => {
                                            if (selectedRateOfReturnOptions.includes(item.value)) {
                                                return (
                                                    <FundTableHeaderCell
                                                        key={index}
                                                        handleDataSort={handleDataSort}
                                                        val={key}
                                                        label={item.label}
                                                        className={
                                                            'sort-period flex ' +
                                                            (globalCtx.layout === 'ofe' || globalCtx.layout === 'dfe'
                                                                ? ' sort-period-ofe'
                                                                : '') +
                                                            (globalCtx.layout !== 'fio' && globalCtx.layout !== 'ofe'
                                                                ? ' sort-period-all'
                                                                : '')
                                                        }
                                                        ctxSortCol={ctx.sortCol}
                                                        ctxSortDir={ctx.sortDir}
                                                    />
                                                )
                                            }
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isLoading ? (
                            [...Array(ctx.limit)].map((_, index) => (
                                <FundTableRow
                                    key={'idx_' + index.toString()}
                                    fund={{} as Fund}
                                    mode={mode}
                                    ctx={ctx}
                                    className={'before-on-load'}
                                />
                            ))
                        ) : calculatedRows.length > 0 ? (
                            <>
                                {isLoader() && (
                                    <>
                                        <div className="loader"></div>
                                        {/*<Oval*/}
                                        {/*    visible={true}*/}
                                        {/*    height="44"*/}
                                        {/*    width="42"*/}
                                        {/*    color="#007AB3"*/}
                                        {/*    ariaLabel="oval-loading"*/}
                                        {/*    wrapperStyle={{}}*/}
                                        {/*    wrapperClass="spiner"*/}
                                        {/*/>*/}
                                    </>
                                )}

                                {calculatedRows.map((_fund: Fund, index: number) => {
                                    return (
                                        <FundTableRow
                                            key={'idx_' + index.toString()}
                                            fund={_fund}
                                            mode={mode}
                                            ctx={ctx}
                                        />
                                    )
                                })}
                            </>
                        ) : (
                            <div className="no-results-filters flex">
                                <span>
                                    Nie znaleziono żadnych funduszy.
                                    <br />
                                    Wyczyść lub zmień filtry.
                                </span>
                            </div>
                        )}
                    </div>
                </SimpleBar>

                {globalCtx.layout !== 'ofe' && globalCtx.layout !== 'dfe' && (
                    <div id="pagination" className="flex">
                        <div className={'show-all ' + (globalCtx.layout === 'all' && 'show-all-inactive flex')}>
                            {' '}
                            {(globalCtx.layout === 'fio' || globalCtx.layout === 'ufk') && ctx.activeTab && (
                                <CompareButton selected={ctx.compared.length} setActiveTab={ctx.setActiveTab} />
                            )}
                        </div>

                        <div className={'pagination flex ' + (paginationRange.length === 1 && 'cursor-none')}>
                            <div className={'arrow flex ' + (paginationRange.length === 1 && 'arrow-opacity')}>
                                <a
                                    href="#fund-table"
                                    className="arrow-parameters"
                                    {...(activePage !== 1 && {onClick: () => setActivePage(1)})}>
                                    <SVGIcon
                                        id={'arrow-first'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                </a>
                                <a
                                    href="#fund-table"
                                    className="arrow-parameters display-none"
                                    {...(activePage !== 1 && {onClick: () => setActivePage(activePage - 1)})}>
                                    <SVGIcon
                                        id={'arrow-left'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                </a>
                            </div>

                            <div className="page flex">
                                {paginationRange.map((num: number) => {
                                    if (num === 0) {
                                        // fix for triple dots
                                        return (
                                            <div key={'page_num_' + Math.floor(Math.random() * (2000 - 1000) + 1000)}>
                                                &#8230;
                                            </div>
                                        )
                                    }

                                    return (
                                        <a
                                            href="#fund-table"
                                            key={'page_num_' + num}
                                            className={num === activePage ? 'page-number active' : 'page-number'}
                                            onClick={() => setActivePage(num)}>
                                            {num}
                                        </a>
                                    )
                                })}
                            </div>

                            <div className={'arrow flex ' + (paginationRange.length === 1 && 'arrow-opacity')}>
                                <a
                                    href="#fund-table"
                                    className={' arrow-parameters display-none '}
                                    {...(activePage !== totalPages && {
                                        onClick: () => setActivePage(activePage + 1),
                                    })}>
                                    <SVGIcon
                                        id={'arrow-right'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                </a>
                                <a
                                    href="#fund-table"
                                    className=" arrow-parameters "
                                    {...(activePage !== totalPages && {
                                        onClick: () => setActivePage(totalPages),
                                    })}>
                                    <SVGIcon
                                        id={'arrow-last'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                </a>
                            </div>
                        </div>
                        <div className="show-all flex">
                            <span>{t('label.catalog.select.qty')}</span>

                            <LimitSelectDropdown
                                options={[
                                    {value: 20, label: t('label.catalog.select.20')},
                                    {value: 50, label: t('label.catalog.select.50')},
                                    {value: 100, label: t('label.catalog.select.100')},
                                    {value: 9999, label: t('label.catalog.select.all')},
                                ]}
                                selected={ctx.limit}
                                toggleOption={handleLimitChange}
                            />
                        </div>
                    </div>
                )}
            </div>
            {globalCtx.layout === 'fio' || globalCtx.layout === 'ufk' ? (
                <div className="download flex">
                    <button className="download-btn flex" onClick={downloadList}>
                        <span className="download-icon">
                            <SVGIcon
                                id={'download-pdf'}
                                className={'light-blue'}
                                width={24}
                                height={24}
                                fill={'#006192'}
                            />
                            <span className="download-text">{t('label.btn.xlsx.download.fund')}</span>
                        </span>
                    </button>
                </div>
            ) : (
                ''
            )}
            {globalCtx.layout === 'ofe' || globalCtx.layout === 'dfe' ? (
                <div className="download download-compared flex" style={{paddingBottom: '32px'}}>
                    <button className="download-btn flex" onClick={downloadList}>
                        <span className="download-icon" style={{gap: '8px'}}>
                            <span className="download-text download-text-xlsx">{t('label.btn.xlsx.download')}</span>
                            <SVGIcon
                                id={'download-pdf'}
                                className={'light-blue'}
                                width={24}
                                height={24}
                                fill={'#006192'}
                            />
                        </span>
                    </button>
                </div>
            ) : (
                ''
            )}
        </>
    )
}

export default FundTable
